import React from "react"
import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Card, Container, Row, Col, Button, Form, Alert } from "react-bootstrap"
import { FaShieldAlt, FaLock, FaEye, FaEyeSlash, FaUserCheck, FaCrown, FaEnvelope, FaPhone } from "react-icons/fa"
import api from "../../services/api"

export default function SuperAdminRegister() {
  const navigate = useNavigate()
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setShowAlert({ show: true, variant: "danger", message: "Please fill in all required fields." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setShowAlert({ show: true, variant: "danger", message: "Passwords do not match." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    if (formData.password.length < 6) {
      setShowAlert({ show: true, variant: "danger", message: "Password must be at least 6 characters long." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    if (!formData.agreeToTerms) {
      setShowAlert({ show: true, variant: "danger", message: "Please agree to the terms and conditions." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    setLoading(true)
    try {
      const response = await api.registerSuperAdmin({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      })
      
      if (response.success) {
        setShowAlert({
          show: true,
          variant: "success",
          message: "Super Admin registration successful! You can now login.",
        })
        setTimeout(() => {
          navigate("/admin/super-admin/login")
        }, 2000)
      } else {
        setShowAlert({ show: true, variant: "danger", message: response.message })
      }
    } catch (error) {
      setShowAlert({ show: true, variant: "danger", message: "Registration failed. Please try again." })
    } finally {
      setLoading(false)
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center position-relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100">
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "200px",
            height: "200px",
            background: "white",
            top: "10%",
            right: "15%",
            animation: "float 6s ease-in-out infinite",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "150px",
            height: "150px",
            background: "white",
            bottom: "15%",
            left: "10%",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        ></div>
      </div>

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            {/* Header */}
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{
                  width: "80px",
                  height: "80px",
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  boxShadow: "0 8px 32px rgba(220, 38, 38, 0.3)",
                }}
              >
                <FaCrown className="text-white" size={32} />
              </div>
              <h2 className="text-white fw-bold mb-2">Super Admin Registration</h2>
              <p className="text-white-50 mb-0">Create a new super admin account</p>
            </div>

            {/* Registration Card */}
            <Card className="border-0 shadow-lg">
              <Card.Body className="p-4">
                {showAlert.show && (
                  <Alert variant={showAlert.variant} dismissible onClose={() => setShowAlert({ show: false, variant: "", message: "" })}>
                    {showAlert.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    {/* Name Field */}
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-muted">
                          <FaUserCheck className="me-2" />
                          Full Name *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          className="border-2"
                          style={{ borderColor: "#e5e7eb" }}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Email Field */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-muted">
                          <FaEnvelope className="me-2" />
                          Email Address *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className="border-2"
                          style={{ borderColor: "#e5e7eb" }}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Phone Field */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-muted">
                          <FaPhone className="me-2" />
                          Phone Number
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          className="border-2"
                          style={{ borderColor: "#e5e7eb" }}
                        />
                      </Form.Group>
                    </Col>

                    {/* Password Field */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-muted">
                          <FaLock className="me-2" />
                          Password *
                        </Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={formData.password}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                            className="border-2 pe-5"
                            style={{ borderColor: "#e5e7eb" }}
                            required
                          />
                          <Button
                            type="button"
                            variant="link"
                            className="position-absolute end-0 top-0 h-100 text-muted"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ zIndex: 10 }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </div>
                        <Form.Text className="text-muted">
                          Password must be at least 6 characters long
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    {/* Confirm Password Field */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-muted">
                          <FaLock className="me-2" />
                          Confirm Password *
                        </Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                            className="border-2 pe-5"
                            style={{ borderColor: "#e5e7eb" }}
                            required
                          />
                          <Button
                            type="button"
                            variant="link"
                            className="position-absolute end-0 top-0 h-100 text-muted"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{ zIndex: 10 }}
                          >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Terms and Conditions */}
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      label={
                        <span>
                          I agree to the{" "}
                          <a href="#" className="text-decoration-none" style={{ color: "#dc2626" }}>
                            Terms and Conditions
                          </a>{" "}
                          and{" "}
                          <a href="#" className="text-decoration-none" style={{ color: "#dc2626" }}>
                            Privacy Policy
                          </a>
                        </span>
                      }
                      checked={formData.agreeToTerms}
                      onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                      required
                    />
                  </Form.Group>

                  {/* Register Button */}
                  <Button
                    type="submit"
                    className="w-100 mb-3 fw-semibold py-2"
                    disabled={loading}
                    style={{
                      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      border: "none",
                      boxShadow: "0 4px 16px rgba(220, 38, 38, 0.3)",
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <FaShieldAlt className="me-2" />
                        Create Super Admin Account
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {/* Footer Links */}
            <div className="text-center mt-4">
              <p className="text-white-50 mb-2">
                Already have a super admin account?{" "}
                <Link to="/admin/super-admin/login" className="text-white text-decoration-none fw-semibold">
                  Login here
                </Link>
              </p>
              <p className="text-white-50 mb-0">
                <Link to="/" className="text-white-50 text-decoration-none">
                  ← Back to Home
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  )
}
